import type { LearnTopic } from '../types';
import { $, $0, width } from '../format';

export const capitalTopic: LearnTopic = {
  id: 'capital',
  tier: 'position',
  title: 'Max loss and what is held',
  summary: "Width minus credit is the most a side can lose per share. Schwab holds the wider side against the trade.",
  body: [
    'The most a credit spread can lose is the distance between its two strikes, minus the credit you collected — per share, times 100 per contract. That is what the wing — the long option that caps the spread — buys you: a known worst case.',
    'Because the stock cannot be both below the short put and above the short call at expiration, an iron condor can only lose on one side. So the broker holds the requirement for the wider side only, not both: the width of the wider spread, times 100, times contracts.',
    'The credit you collected is already in your account; it is not subtracted from what is held. That is why the held figure can look large next to a small credit: it is the insurance deposit, and it comes back when the position expires or closes.',
  ],
  explain(ctx) {
    const { shortPut, longPut, shortCall, longCall, credit, held } = ctx;
    const sentences: string[] = [];

    /**
     * Width minus credit is the worst case of a CREDIT spread only. Orientation, not the sign of
     * the net premium, decides which one this is: a put side is a credit spread when the short put
     * sits above the long one, a call side when the short call sits below the long one. A debit
     * vertical (long 105 put / short 100 put, bought for $0.30) loses at most what it cost —
     * printing width minus credit there reports $5.30 for a $0.30 risk.
     */
    const sideSentence = (side: 'put' | 'call', spreadWidth: number, isCredit: boolean) => {
      if (!isCredit) {
        return credit == null
          ? `Your ${side} spread is ${spreadWidth} wide and was bought as a debit, which is the most it can lose.`
          : `Your ${side} spread is ${spreadWidth} wide and was bought for ${$(-credit)} per share, which is the most it can lose.`;
      }
      if (credit == null) {
        return `Your ${side} spread is ${spreadWidth} wide, so its worst case is ${$(spreadWidth)} per share before credit, ${$0(spreadWidth * 100)} per contract.`;
      }
      return `Your ${side} spread is ${spreadWidth} wide, so its worst case is ${$(spreadWidth - credit)} per share, ${$0((spreadWidth - credit) * 100)} per contract.`;
    };

    if (shortPut != null && longPut != null) {
      sentences.push(sideSentence('put', width(shortPut, longPut), shortPut > longPut));
    }
    if (shortCall != null && longCall != null) {
      sentences.push(sideSentence('call', width(longCall, shortCall), shortCall < longCall));
    }
    if (sentences.length === 0) {
      sentences.push('This position has no spread with both strikes open, so there is no width to measure a worst case against.');
    }

    sentences.push(
      held != null
        ? `Schwab holds ${$0(held)} against this position: for each expiration still open, the wider side's width, times 100, times contracts.`
        : 'The held amount cannot be computed here: an uncovered short has no fixed requirement, and an unpriced leg cannot be measured.',
    );
    // A debit position reaches this topic too (appliesTo is always true), and "The -$0.30 credit"
    // is both wrong and unreadable. Branch on the sign; a flat zero gets no sentence at all.
    if (credit != null && credit > 0) {
      sentences.push(
        `The ${$(credit)} credit is already in your cash; it is shown beside the held figure, not subtracted from it.`,
      );
    } else if (credit != null && credit < 0) {
      sentences.push(
        `You paid ${$(-credit)} per share for this position; that is already out of your cash and is the most it can lose.`,
      );
    }
    return sentences;
  },
  appliesTo() {
    return true;
  },
};
